// src/pages/RecipientsPage.jsx
import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';  // ← ADD THIS LINE
import {
  getRecipients,
  deleteRecipient,
  toggleRecipientFavorite,
  verifyBankAccount,
} from '../services/api';
import AddRecipientModal from '../components/AddRecipientModal';
import './RecipientsPage.css';

function RecipientsPage() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadRecipients();
  }, [searchTerm, filterType, filterCountry, showFavoritesOnly]);

  const loadRecipients = async () => {
    try {
      setLoading(true);
      setError('');
      
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (filterType) filters.recipientType = filterType;
      if (filterCountry) filters.recipientCountry = filterCountry;
      if (showFavoritesOnly) filters.isFavorite = true;
      
      const response = await getRecipients(filters);
      
      if (response.success) {
        setRecipients(response.recipients || []);
      } else {
        setError(response.message || 'Failed to load recipients');
      }
    } catch (err) {
      setError(err.message || 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingRecipient(null);
    setShowModal(true);
  };

  const handleEdit = (recipient) => {
    setEditingRecipient(recipient);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingRecipient(null);
  };

  const handleModalSuccess = (message) => {
    setShowModal(false);
    setEditingRecipient(null);
    setSuccess(message);
    setTimeout(() => setSuccess(''), 5000);
    loadRecipients();
  };

  const handleToggleFavorite = async (recipientId) => {
    try {
      const response = await toggleRecipientFavorite(recipientId);
      if (response.success) {
        setSuccess(response.message);
        setTimeout(() => setSuccess(''), 3000);
        loadRecipients();
      } else {
        setError(response.message || 'Failed to toggle favorite');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(err.message || 'Failed to toggle favorite');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteClick = (recipient) => {
    setDeleteConfirm(recipient);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    
    try {
      setDeleting(true);
      setError('');
      
      const response = await deleteRecipient(deleteConfirm.id);
      
      if (response.success) {
        setSuccess(response.message);
        setTimeout(() => setSuccess(''), 5000);
        setDeleteConfirm(null);
        loadRecipients();
      } else {
        setError(response.message || 'Failed to delete recipient');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete recipient');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const handleVerifyBank = async (recipientId) => {
    try {
      setError('');
      setSuccess('Verifying bank account...');
      
      const response = await verifyBankAccount(recipientId);
      
      if (response.success) {
        setSuccess(response.message || 'Bank account verified successfully');
        setTimeout(() => setSuccess(''), 5000);
        loadRecipients();
      } else {
        setError(response.message || 'Failed to verify bank account');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(err.message || 'Failed to verify bank account');
      setTimeout(() => setError(''), 5000);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('');
    setFilterCountry('');
    setShowFavoritesOnly(false);
  };

  const getRecipientTypeLabel = (type) => {
    switch (type) {
      case 'mobile_money': return 'Mobile Money';
      case 'bank_account': return 'Bank Account';
      case 'cash_pickup': return 'Cash Pickup';
      default: return type;
    }
  };

  const getCountryFlag = (countryCode) => {
    const flags = {
      KE: '🇰🇪', UG: '🇺🇬', TZ: '🇹🇿', RW: '🇷🇼',
      GH: '🇬🇭', NG: '🇳🇬', ZA: '🇿🇦', ET: '🇪🇹',
    };
    return flags[countryCode] || '🌍';
  };

  return (
    <>
      <Navigation />
      <div className="recipients-page">
        <div className="page-header">
          <div className="header-left">
            <h1>Recipients</h1>
            <p className="subtitle">Manage your saved recipients for faster transfers</p>
          </div>
          <button className="btn btn-primary" onClick={handleAddNew}>
            <span className="icon">+</span> Add Recipient
          </button>
        </div>

        {success && (
          <div className="alert alert-success">
            <span className="icon">✓</span>
            {success}
          </div>
        )}
        {error && (
          <div className="alert alert-error">
            <span className="icon">⚠</span>
            {error}
          </div>
        )}

        <div className="filters-section">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
            <option value="">All Types</option>
            <option value="mobile_money">Mobile Money</option>
            <option value="bank_account">Bank Account</option>
            <option value="cash_pickup">Cash Pickup</option>
          </select>

          <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} className="filter-select">
            <option value="">All Countries</option>
            <option value="KE">🇰🇪 Kenya</option>
            <option value="UG">🇺🇬 Uganda</option>
            <option value="TZ">🇹🇿 Tanzania</option>
            <option value="RW">🇷🇼 Rwanda</option>
            <option value="GH">🇬🇭 Ghana</option>
            <option value="NG">🇳🇬 Nigeria</option>
          </select>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showFavoritesOnly}
              onChange={(e) => setShowFavoritesOnly(e.target.checked)}
            />
            <span>⭐ Favorites Only</span>
          </label>

          {(searchTerm || filterType || filterCountry || showFavoritesOnly) && (
            <button className="btn btn-secondary" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>

        <div className="recipients-table-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading recipients...</p>
            </div>
          ) : recipients.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No Recipients Found</h3>
              <p>
                {searchTerm || filterType || filterCountry || showFavoritesOnly
                  ? 'Try adjusting your filters'
                  : 'Add your first recipient to get started'}
              </p>
              {!(searchTerm || filterType || filterCountry || showFavoritesOnly) && (
                <button className="btn btn-primary" onClick={handleAddNew}>
                  Add Recipient
                </button>
              )}
            </div>
          ) : (
            <table className="recipients-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Recipient</th>
                  <th>Phone</th>
                  <th>Country</th>
                  <th>Type</th>
                  <th>Details</th>
                  <th>Transactions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((recipient) => (
                  <tr key={recipient.id}>
                    <td>
                      <button
                        className={`favorite-btn ${recipient.isFavorite ? 'active' : ''}`}
                        onClick={() => handleToggleFavorite(recipient.id)}
                        title={recipient.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {recipient.isFavorite ? '⭐' : '☆'}
                      </button>
                    </td>
                    <td>
                      <div className="recipient-name">
                        <strong>{recipient.recipientName}</strong>
                        {recipient.nickname && (
                          <span className="nickname">"{recipient.nickname}"</span>
                        )}
                        {recipient.relationship && (
                          <span className="relationship">{recipient.relationship}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="phone">{recipient.recipientPhone}</span>
                    </td>
                    <td>
                      <span className="country">
                        {getCountryFlag(recipient.recipientCountry)} {recipient.recipientCountry}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${recipient.recipientType}`}>
                        {getRecipientTypeLabel(recipient.recipientType)}
                      </span>
                    </td>
                    <td>
                      <div className="recipient-details">
                        {recipient.recipientType === 'bank_account' && (
                          <>
                            <div className="detail-item">
                              <strong>{recipient.bankName}</strong>
                            </div>
                            <div className="detail-item">
                              Acc: {recipient.bankAccountNumber}
                            </div>
                            {recipient.equityValidationStatus && (
                              <span className={`validation-badge ${recipient.equityValidationStatus}`}>
                                {recipient.equityValidationStatus === 'verified' ? '✓ Verified' : 
                                 recipient.equityValidationStatus === 'failed' ? '✗ Failed' : 
                                 '⏳ Pending'}
                              </span>
                            )}
                          </>
                        )}
                        {recipient.recipientType === 'mobile_money' && (
                          <div className="detail-item">M-Pesa</div>
                        )}
                        {recipient.recipientType === 'cash_pickup' && (
                          <div className="detail-item">Cash Pickup Available</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="transaction-count">
                        {recipient.transactionCount || 0}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon btn-edit" onClick={() => handleEdit(recipient)} title="Edit recipient">
                          ✏️
                        </button>
                        {recipient.recipientType === 'bank_account' && !recipient.equityValidationStatus && (
                          <button className="btn-icon btn-verify" onClick={() => handleVerifyBank(recipient.id)} title="Verify bank account">
                            ✓
                          </button>
                        )}
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDeleteClick(recipient)}
                          title="Delete recipient"
                          disabled={recipient.transactionCount > 0}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && recipients.length > 0 && (
          <div className="recipients-count">
            Showing {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
          </div>
        )}

        {showModal && (
          <AddRecipientModal
            recipient={editingRecipient}
            onClose={handleModalClose}
            onSuccess={handleModalSuccess}
          />
        )}

        {deleteConfirm && (
          <div className="modal-overlay" onClick={handleDeleteCancel}>
            <div className="modal delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Recipient</h3>
                <button className="close-btn" onClick={handleDeleteCancel}>×</button>
              </div>
              <div className="modal-body">
                <div className="warning-icon">⚠️</div>
                <p>Are you sure you want to delete <strong>{deleteConfirm.recipientName}</strong>?</p>
                <p className="warning-text">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleDeleteCancel} disabled={deleting}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={handleDeleteConfirm} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default RecipientsPage;